/**
 * "Is this block type used anywhere else?"
 *
 * The assistant edits the block DEFINITION (`builder_def`), not the instance, so
 * its change lands on every instance of the type — on every page and every
 * dashboard. We say so before the editor writes their request, rather than let
 * them find out afterwards.
 *
 * Informative, never blocking: a network failure or a 403 simply leaves the
 * context chip without a warning.
 *
 * Deuxième lecteur depuis : la pop-in de suppression du catalogue, qui demande
 * la LISTE des contenus (`limit`) et non plus seulement le compte — même
 * question, même endpoint.
 */

const ENDPOINT = '/api/builder/block-usage'

/**
 * @param {string} type — block type machine name (e.g. `HeroBlock`)
 * @param {AbortSignal?} signal
 * @param {{limit?: number}} [options] — taille des listes renvoyées (pages,
 *   tableaux de bord). Le défaut du serveur (5) suffit à la puce du chat, qui
 *   n'affiche qu'un compte.
 * @returns {Promise<{instances:number, pages:Array, pagesTotal:number,
 *   dashboards:number, dashboardList:Array, totalUsage:number}|null>}
 */
export async function fetchBlockUsage(type, signal, { limit } = {}) {
  if (!type) return null
  try {
    const query = new URLSearchParams({ type })
    if (limit) query.set('limit', String(limit))
    const res = await fetch(`${ENDPOINT}?${query}`, {
      credentials: 'include',
      cache: 'no-store',
      signal,
    })
    if (!res.ok) return null
    return await res.json()
  }
  catch {
    return null
  }
}

const COUNTS_ENDPOINT = '/api/builder/def-usage'

/**
 * Le nombre de contenus de CHAQUE définition de bloc, en une requête :
 * `{HeroBlock: 12, …}` (instances + tableaux de bord).
 *
 * Troisième lecteur : le filtre « Non utilisés » du catalogue. Le compte est
 * celui qui autorise la suppression (`totalUsage`, aveugle aux droits) — un bloc
 * que ce filtre montre est donc un bloc que « Supprimer » acceptera.
 *
 * @param {AbortSignal?} signal
 * @returns {Promise<Record<string, number>|null>} NULL si la question n'a pas pu
 *   être posée (réseau, 403 : la route exige le droit d'utiliser le builder).
 */
export async function fetchDefUsageCounts(signal) {
  try {
    const res = await fetch(COUNTS_ENDPOINT, {
      credentials: 'include',
      cache: 'no-store',
      signal,
    })
    if (!res.ok) return null
    const body = await res.json()
    return body?.counts && typeof body.counts === 'object' ? body.counts : null
  }
  catch {
    return null
  }
}

/**
 * The warning sentence, or `null` when there is nothing to report (a single
 * instance — the one the editor is looking at).
 *
 * The count is TOTAL (current location included) rather than "the others": the
 * page list is capped server-side, so the current location cannot reliably be
 * subtracted from it. "Used in N places" is true in every case; "N others"
 * would not be.
 */
export function usageWarning(usage) {
  if (!usage) return null
  const instances = usage.instances ?? 0
  const pages = usage.pagesTotal ?? 0
  const dashboards = usage.dashboards ?? 0
  if (instances + dashboards <= 1) return null

  const places = []
  if (pages > 0) places.push(`${pages} page${pages > 1 ? 's' : ''}`)
  if (dashboards > 0) places.push(`${dashboards} tableau${dashboards > 1 ? 'x' : ''} de bord`)
  const where = places.length ? ` (${places.join(', ')})` : ''
  const total = instances + dashboards
  return `Ce type de bloc est utilisé à ${total} endroits${where} : la modification s'appliquera partout, pas seulement ici.`
}
